import { ActiveSelection, Canvas, FabricImage, FabricObject } from 'fabric';
import { DrawStyle } from './drawing-tool.model';
import { DashedPencilBrush } from './dashed-pencil-brush';

/** Options for {@link applyStylesToSelection}. */
export interface ApplyStylesOptions {
    /**
     * Custom sync for the restyled objects. When provided, it is called with the
     * objects that were restyled and `object:modified` is NOT fired — lets a consumer
     * sync only the changed style props (e.g. a binding's `syncStylesOnly`) instead of
     * a full object sync. When omitted, `object:modified` is fired (default) so any
     * attached binding persists the change.
     */
    onRestyled?: (objects: FabricObject[]) => void;
}

/**
 * Re-style the canvas's currently-selected object(s): apply stroke color, width, and
 * brush-style (dash) to the active object or every object in a multi-selection.
 * Image objects are skipped (no stroke). Framework-agnostic: it never calls any app's
 * Yjs service directly. By default it fires `object:modified` (carrying the
 * `ActiveSelection` for a multi-selection) so the attached binding persists the change;
 * pass {@link ApplyStylesOptions.onRestyled} to sync styles-only instead.
 *
 * @returns true if at least one object was restyled, false otherwise.
 */
export function applyStylesToSelection(
    canvas: Canvas,
    style: DrawStyle,
    options?: ApplyStylesOptions
): boolean {
    // Guard against minimal mocks / canvases without selection support.
    if (typeof canvas.getActiveObject !== 'function') return false;
    const activeObject = canvas.getActiveObject();
    if (!activeObject) return false;

    const strokeDashArray = DashedPencilBrush.getDashArray(style.brushStyle, style.width);
    const modified: FabricObject[] = [];

    if (activeObject.type === 'activeselection') {
        for (const obj of (activeObject as ActiveSelection).getObjects()) {
            if (obj instanceof FabricImage) continue; // images have no stroke
            obj.set({ stroke: style.color, strokeWidth: style.width, strokeDashArray });
            modified.push(obj);
        }
    } else if (!(activeObject instanceof FabricImage)) {
        activeObject.set({ stroke: style.color, strokeWidth: style.width, strokeDashArray });
        modified.push(activeObject);
    }

    if (modified.length === 0) return false;

    if (options?.onRestyled) {
        options.onRestyled(modified);
    } else {
        // The event carries the active object (a single object or the ActiveSelection,
        // which the binding expands to sync each member).
        canvas.fire('object:modified', { target: activeObject });
    }
    canvas.renderAll();
    return true;
}
